// src/pages/ProjectDetail/ProjectDetail.js
//
// Project detail page — top-level. Fetches one project by id, parses the
// article, and composes the page from small components.
//
// Performance notes:
// - useProject() does a single Firestore document read, not a collection scan.
// - parseArticle() is synchronous, runs in useMemo, so text paints on first
//   render without waiting on images.
// - All <img> tags are native — the browser handles caching and decoding,
//   so the old IndexedDB blob layer is gone.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, useScroll } from 'framer-motion';
import { useProject } from '../../hooks/useProjects';
import { parseArticle } from '../../utils/parseArticle';
import ProjectHero from './components/ProjectHero';
import ProjectArticle from './components/ProjectArticle';
import ProjectGallery from './components/ProjectGallery';
import ProjectVideos from './components/ProjectVideos';
import ProjectPager from './components/ProjectPager';
import Lightbox from './components/Lightbox';
import './ProjectDetail.css';

export default function ProjectDetail() {
  const { id } = useParams();
  const { project, loading, error } = useProject(id);
  const [lightbox, setLightbox] = useState(null);
  const { scrollYProgress } = useScroll();

  // Pager navigation stays on this route, so react-router keeps the scroll
  // position — reset it whenever the project changes.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Parse the description into blocks. Pure, synchronous, memoized.
  const { blocks, unusedImages } = useMemo(() => {
    if (!project) return { blocks: [], unusedImages: [] };
    const { blocks, unusedImageIndices } = parseArticle(
      project.longDescription,
      project.additionalImages
    );
    // Normalize gallery images to the same shape the article uses.
    const unusedImages = unusedImageIndices.map((i) => {
      const raw = project.additionalImages[i];
      if (typeof raw === 'string') {
        return { src: raw, thumbnailSrc: null, caption: null, index: i };
      }
      return {
        src: raw.src,
        thumbnailSrc: raw.thumbnailSrc || null,
        caption: raw.caption || null,
        index: i,
      };
    });
    return { blocks, unusedImages };
  }, [project]);

  const openImage = useCallback((image) => {
    setLightbox({
      type: 'image',
      src: image.src,
      caption: image.caption,
    });
  }, []);

  const openVideo = useCallback((src) => {
    setLightbox({ type: 'video', src });
  }, []);

  const closeLightbox = useCallback(() => setLightbox(null), []);

  if (loading) {
    return (
      <div className="pd-state">
        <div className="pd-state__spinner" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="pd-state">
        <h2>Project not found</h2>
        <p>The project you're looking for doesn't exist or couldn't be loaded.</p>
        <Link to="/projects" className="pd-state__back">
          ← Back to projects
        </Link>
      </div>
    );
  }

  return (
    <div className="pd-page">
      <motion.div
        className="pd-progress"
        style={{ scaleX: scrollYProgress }}
        aria-hidden="true"
      />

      <ProjectHero project={project} onOpenImage={openImage} />

      <main className="pd-main">
        <ProjectArticle
          summary={project.description}
          blocks={blocks}
          onOpenImage={openImage}
        />

        <ProjectGallery
          images={unusedImages}
          onOpenImage={openImage}
          title="More images"
        />

        <ProjectVideos
          videoUrls={project.videoUrls}
          demoUrl={project.demoUrl}
          onOpenVideo={openVideo}
        />

        <ProjectPager currentId={project.id} />
      </main>

      <Lightbox item={lightbox} onClose={closeLightbox} />
    </div>
  );
}
